import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'

import { getFirebase } from '~/firebase'

import { bootcampSanity } from '~/shared/sanity/bootcamp'
import { bootcampOverview, bootcampOverviewModule, BootcampOverviewUserData } from '~/types/bootcamp'

import { bootcampSplitApi } from './split.api'

const GetBootcampOverview = bootcampSplitApi.injectEndpoints({
  endpoints: (build) => ({
    getBootcampOverview: build.query<bootcampOverview, { key: string; language: string; userId: number | null }>({
      queryFn: async ({ key, language, userId }) => {
        try {
          const { fireStore, query, getDocs, collection, where } = await getFirebase()
          const QueryPathRef =
            userId && userId > 0
              ? getDocs(
                  query(collection(fireStore, 'bootcamp', String(userId), 'progress'), where('bootcampId', '==', key)),
                )
              : null

          const [data, userData] = await Promise.all([bootcampSanity.bootcampsList({ key, language }), QueryPathRef])

          const processedData = userData?.docs.map((item) => {
            const page = item.data()
            return {
              moduleId: page.moduleId,
              bossDefeated: page.quiz ? page.quiz > 0 : false,
              chests: (page.green ?? 0) + (page.red ?? 0) + (page.blue ?? 0),
            }
          })

          const result = {
            name: data?.bootcamps?.name,
            intro: data?.bootcamps?.intro,
            modules: data?.bootcamps?.modules?.map<bootcampOverviewModule>((res) => {
              const totalChests =
                res?.chests?.reduce<number>((a, b) => {
                  const activities =
                    b?.activities?.reduce<number>((c, d) => {
                      const difficulties =
                        d?.difficulties?.reduce<number>((e, f) => {
                          return e + (f ?? 0)
                        }, 0) ?? 0
                      return c + difficulties
                    }, 0) ?? 0
                  return a + activities
                }, 0) ?? 0

              const moduleProgress = processedData?.find((x) => x.moduleId === res.moduleNumber)
              const progress = totalChests > 0 ? ((moduleProgress?.chests ?? 0) / totalChests) * 100 : 0

              return {
                name: res.name,
                moduleNumber: res.moduleNumber,
                levels: res.levels,
                bossDefeated: moduleProgress?.bossDefeated ?? false,
                progress,
                chests: {
                  total: totalChests,
                  collected: moduleProgress?.chests ?? 0,
                },
                badgeImage: res.badgeImage,
                badgeOutlineImage: res.badgeOutlineImage,
              }
            }),
          }

          return {
            data: result as bootcampOverview,
          }
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          }
        }
      },
      providesTags: ['Overview'],
    }),
    getUserVideoCoursesData: build.query<BootcampOverviewUserData[], { userId: number | null; key: string }>({
      queryFn: async ({ userId, key }) => {
        try {
          const { fireStore, query, getDocs, collection, where } = await getFirebase()
          const QueryPathRef =
            userId && userId > 0
              ? getDocs(
                  query(collection(fireStore, 'bootcamp', String(userId), 'progress'), where('bootcampId', '==', key)),
                )
              : null

          const userData = await QueryPathRef

          const processedData = userData
            ? userData.docs.map((item) => {
                const page = item.data()
                return {
                  moduleId: page.moduleId,
                  bossDefeated: page.quiz ? page.quiz > 0 : false,
                  chests: (page.green ?? 0) + (page.red ?? 0) + (page.blue ?? 0),
                }
              })
            : []

          return {
            data: processedData ?? ([] as BootcampOverviewUserData[]),
          }
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          }
        }
      },
      providesTags: ['Overview User Data'],
    }),
    getUserVideoCoursesDataList: build.query<BootcampOverviewUserData[], { userId: number | null; gymType: string }>({
      queryFn: async ({ userId, gymType }) => {
        try {
          const { fireStore, query, getDocs, collection, where } = await getFirebase()
          const QueryPathRef =
            userId && userId > 0
              ? getDocs(
                  query(collection(fireStore, 'bootcamp', String(userId), 'progress'), where('gymType', '==', gymType)),
                )
              : null

          const userData = await QueryPathRef
          const processedData = userData
            ? userData.docs.map((item) => {
                const page = item.data()

                return {
                  bootcampId: page.bootcampId,
                  moduleId: page.moduleId,
                  bossDefeated: page.quiz ? page.quiz > 0 : false,
                  chests: (page.green ?? 0) + (page.red ?? 0) + (page.blue ?? 0),
                }
              })
            : []

          return {
            data: processedData ?? ([] as BootcampOverviewUserData[]),
          }
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          }
        }
      },
      providesTags: ['Overview User Data'],
    }),
  }),
  overrideExisting: false,
})

export const { useGetBootcampOverviewQuery, useGetUserVideoCoursesDataQuery, useGetUserVideoCoursesDataListQuery } =
  GetBootcampOverview
